import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { initiateRefreshFlow, logout } from 'tempus-okta-client';

import SessionTimeoutModal from '~/components/_modals/SessionTimeoutModal/SessionTimeoutModal';

// Time values in milliseconds
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes of inactivity
const COUNTDOWN_DURATION = 2 * 60 * 1000; // Countdown duration of 2 minutes before auto-logout
const CHECK_INTERVAL = 10 * 1000; // Check every 10 seconds
const DEFAULT_COUNTDOWN = null; // The countdown defaults to null; only shows when a real number is set
const TOKEN_EXPIRATION_KEY = 'tempus-okta-client-expires-at'; // Defined by Tempus Okta package, not exported

const SessionTimeoutManager: React.FC = () => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(DEFAULT_COUNTDOWN);

  const checkIntervalId = useRef<NodeJS.Timeout | null>(null);
  const logoutTriggeredRef = useRef<boolean>(false);

  // Function to get Okta token expiration
  const getOktaTokenExpiration = useCallback(() => {
    try {
      // Get token from localStorage - the token is stored by the Okta client
      const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRATION_KEY));
      if (!expiresAt) {
        throw new Error('No token expiration found');
      }

      return expiresAt;
    } catch (error) {
      console.error(error);
    }

    return null;
  }, []);

  // Function to start the countdown timer
  const startCountdown = useCallback(() => {
    logoutTriggeredRef.current = false;
    setCountdownSeconds(Math.floor(COUNTDOWN_DURATION / 1000));
    setShowTimeoutWarning(true);
  }, []);

  // Function to handle timeout - log the user out
  const handleTimeout = useCallback(() => {
    logout();
  }, []);

  // Function to reset the warning state
  const resetWarningState = useCallback(() => {
    setShowTimeoutWarning(false);
    setCountdownSeconds(DEFAULT_COUNTDOWN);
  }, []);

  // Function to handle user becoming idle
  const onIdle = useCallback(() => {
    if (!showTimeoutWarning) {
      startCountdown();
    }
  }, [showTimeoutWarning, startCountdown]);

  // Setup the idle timer
  const { reset: resetIdleTimer } = useIdleTimer({
    timeout: INACTIVITY_TIMEOUT,
    onIdle,
    debounce: 500,
  });

  // Function to continue the session
  const continueSession = useCallback(async () => {
    resetWarningState();
    resetIdleTimer(); // Reset the idle timer when continuing session
    await initiateRefreshFlow();
  }, [resetIdleTimer, resetWarningState]);

  // Function to start the session check interval for token expiration
  const startSessionCheck = useCallback(() => {
    checkIntervalId.current = setInterval(() => {
      const now = Date.now();

      // Check token expiration
      const tokenExpirationTime = getOktaTokenExpiration();
      if (tokenExpirationTime) {
        const timeToTokenExpiration = tokenExpirationTime - now;
        if (timeToTokenExpiration <= COUNTDOWN_DURATION && !showTimeoutWarning) {
          startCountdown();
        }
      }
    }, CHECK_INTERVAL);
  }, [showTimeoutWarning, getOktaTokenExpiration, startCountdown]);

  useEffect(() => {
    if (countdownSeconds === DEFAULT_COUNTDOWN) {
      return;
    }

    if (countdownSeconds <= 0) {
      if (!logoutTriggeredRef.current) {
        logoutTriggeredRef.current = true;
        handleTimeout();
      }

      setShowTimeoutWarning(false);
      setCountdownSeconds(DEFAULT_COUNTDOWN);
      return;
    }

    const timer = setTimeout(() => {
      setCountdownSeconds((prev) => {
        if (prev === DEFAULT_COUNTDOWN || prev === null || prev <= 0) {
          return prev;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdownSeconds, handleTimeout]);

  // Initialize the token expiration check interval
  useEffect(() => {
    startSessionCheck();

    return () => {
      if (checkIntervalId.current) {
        clearInterval(checkIntervalId.current);
      }
    };
  }, [startSessionCheck]);

  // If countdownSeconds is not set, we will never show the modal
  if (!countdownSeconds) {
    return null;
  }

  return (
    <SessionTimeoutModal
      isOpen={showTimeoutWarning}
      countdownSeconds={countdownSeconds}
      onContinue={continueSession}
      onLogout={handleTimeout}
    />
  );
};

export default SessionTimeoutManager;
